import React, { useState } from 'react';
import { Box, Flex, IconV2, TextV2 } from '@withjoy/joykit';
import { Close } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useUserConsent } from '@shared/utils/userConsent';
import { useListingsPanelTelemetry } from '@apps/stays/AccommodationsConsole/components/ListingsPanel/ListingsPanel.telemetry';
import { CASH_FUND_PAGE_BACKGROUND } from './cashFund.constants';
import balloonIllustration from './assets/honeymoon-fund-balloon.png';

// Once-per-list promo banner for the cash-fund variant (Figma node 53294:10998).
// Cream card + gold balloon illustration + savings copy.
//
// TODO(TRAV-3348 follow-up): the "$340 more in savings" figure is static copy until the BE
// returns a per-search total fund savings amount (see `cashFundSavingsTotalString` below).
export const CashFundBanner = () => {
  const { tq } = useTranslation('accommodationsConsole');
  const telemetry = useListingsPanelTelemetry();
  // Persist dismissal via user consent — stored server-side per user + per event, so the banner
  // stays hidden across reloads/sessions AND follows the couple across devices. Local state hides
  // it immediately on click since the consent write is async.
  const { getUserConsent, setUserConsent, loadingUserConsent } = useUserConsent();
  const [isDismissed, setIsDismissed] = useState(false);

  const hasSeen = getUserConsent('hasSeenCashFundBanner') === true;

  if (loadingUserConsent || hasSeen || isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    telemetry.cashFundBannerDismissed();
    setIsDismissed(true);
    void setUserConsent('hasSeenCashFundBanner', true);
  };

  return (
    // Outer wrapper provides the vertical spacing via PADDING (not margin): the listings column's
    // SkeletonGroup is only a block-formatting context on desktop (overflowY: auto), so on mobile a
    // `marginTop` on the card would collapse through the parent and show no gap. Padding never
    // collapses, so the top space is reliable on both. Horizontal margins inset the card to align
    // with the hotel tiles; `maxWidth` caps it so the fit-content column never grows.
    <Box
      paddingTop={{ _: 7, sm2: 8 }}
      paddingBottom={2}
      marginLeft={6}
      marginRight={{ _: 6, sm2: 8 }}
      width="auto"
      maxWidth={{ md: pxToRem(757) }}
    >
      <Flex
        position="relative"
        backgroundColor={CASH_FUND_PAGE_BACKGROUND}
        borderRadius={3}
        paddingX={{ _: 5, sm2: 8 }}
        paddingY={{ _: 5, sm2: 6 }}
        width="100%"
        alignItems="center"
        columnGap={{ _: 3, sm2: 4 }}
        boxShadow="0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 4px 16px 0px rgba(0, 0, 0, 0.07)"
      >
        <Box
          flexShrink={0}
          width={{ _: pxToRem(64), sm2: pxToRem(101) }}
        >
          <img
            src={balloonIllustration}
            alt=""
            style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }}
          />
        </Box>
        <Flex
          flexDirection="column"
          rowGap={{ _: 1, sm2: 2 }}
          flex="1 0 0"
          minWidth={0}
          paddingRight={{ _: 5, sm2: 6 }}
        >
          <TextV2
            typographyVariant={{ _: 'hed3', sm2: 'hed4' }}
            fontWeight={600}
            color="mono14"
          >
            {tq`Your Honeymoon Fund Goes Further on Joy`}
          </TextV2>
          <TextV2
            typographyVariant={{ _: 'label2', sm2: 'body1' }}
            color="mono12"
          >
            {tq`Browse stays where your fund unlocks special pricing — only on Joy and receive up to $340 more in savings.`}
          </TextV2>
        </Flex>
        <Box
          as="button"
          type="button"
          aria-label={tq`Dismiss`}
          onClick={handleDismiss}
          position="absolute"
          top={5}
          right={5}
          background="none"
          border="none"
          padding={0}
          cursor="pointer"
          lineHeight={0}
        >
          <IconV2
            size="sm"
            color="mono12"
          >
            <Close />
          </IconV2>
        </Box>
      </Flex>
    </Box>
  );
};
